import { Dispatch } from 'redux';
import { IAuth, IUser } from '../../../models';
import { AuthService } from '../../../services/authService';
import { resetClient } from '../../../services/wasteManagementClient';
import { getGeodataToken } from '../geodata/geodataCreators';
import { Actions as GeoActions } from '../geodata/geodataActions';
import { resetState as resetRouteState } from '../route/routeCreators';
import { AuthConstants } from './authConstants';
import { Auth0ContextInterface, User } from '@auth0/auth0-react';

import {
  Actions,
  LoginRequest,
  LogoutRequest,
  SetImpersonatedUser,
} from './authActions';

export function createLoginRequest(payload: IAuth): LoginRequest {
  return {
    type: AuthConstants.USERS_LOGIN_REQUEST,
    payload,
  };
}

export function createLoginSuccess(payload: IUser): LoginRequest.Success {
  return {
    type: AuthConstants.USERS_LOGIN_SUCCESS,
    payload,
  };
}

export function createLoginFailure(error: Error): LoginRequest.Failure {
  return {
    type: AuthConstants.USERS_LOGIN_FAILURE,
    error,
  };
}

export function createLogoutRequest(): LogoutRequest {
  return {
    type: AuthConstants.USERS_LOGOUT_REQUEST,
  };
}

export function createLogoutSuccess(): LogoutRequest.Success {
  return {
    type: AuthConstants.USERS_LOGOUT_SUCCESS,
  };
}

export function createLogoutFailure(error: Error): LogoutRequest.Failure {
  return {
    type: AuthConstants.USERS_LOGOUT_FAILURE,
    error,
  };
}

export function createSetAccessToken(payload: string): LoginRequest.SetToken {
  return {
    type: AuthConstants.SET_ACCESS_TOKEN,
    payload,
  };
}

export function createSetImpersonatedUser(payload: any): SetImpersonatedUser {
  return {
    type: AuthConstants.SET_IMPERSONATED_USER,
    payload,
  };
}

function needsUserInteraction(e: any) {
  const code = e?.error || e?.code || e?.message || '';
  return ['login_required', 'consent_required', 'interaction_required']
    .some(k => code.includes(k));
}

export function loginWithAuth0(auth0: Auth0ContextInterface<User>) {
  return async (dispatch: Dispatch<Actions | GeoActions>): Promise<void> => {
    try {
      // 1) Try silent first
      const accessToken = await auth0.getAccessTokenSilently();
      dispatch(createSetAccessToken(accessToken));
      const user: IAuth = { username: auth0.user?.email ?? '', password: accessToken };
      const result: IUser = await AuthService.loginWithAuth0(user);
      dispatch(createLoginSuccess(result));
      await getGeodataToken()(dispatch);
      return;
    } catch (e: any) {
      if (needsUserInteraction(e)) {
        // Chrome path: no cookie → do a top-level redirect
        await auth0.loginWithRedirect({
          appState: { returnTo: window.location.pathname + window.location.search },
        });
        
      await getGeodataToken()(dispatch);
        return; 
      }

      dispatch(createLoginFailure(e));
      return;
    }
  };
}

export function logout(
  auth0: Auth0ContextInterface<User>
): (dispatch: Dispatch) => Promise<void> {
  return async function (dispatch: Dispatch): Promise<void> {
    dispatch(createLogoutRequest());
    try {
      dispatch(createLogoutSuccess());
      AuthService.logout(auth0);
    } catch (error) {
      console.error(error);
      dispatch(createLogoutFailure(error));
    }
    resetClient();
  };
}

export function setImpersonatedUser(
  user: any
): (dispatch: Dispatch) => Promise<void> {
  return async function (dispatch: Dispatch): Promise<void> {
    /** @todo: reset all relevant states when possible */
    dispatch(resetRouteState());
    dispatch(createSetImpersonatedUser(user));
  };
}

export function resetImpersonatedUser(): ReturnType<
  typeof createSetImpersonatedUser
> {
  return createSetImpersonatedUser(null);
}
